/**
 * `AgentChat.PromptInput` / `AgentChat.Submit` — shadcn's Prompt Input pattern
 * (https://ui.shadcn.com/docs/ai-elements/base/prompt-input): a form around an
 * InputGroup, Enter submits through `requestSubmit`, Shift+Enter is a newline.
 *
 * No attachments: nothing downstream of `actions.submit` carries files to the
 * engine, so an attach button would collect files only to drop them.
 *
 * `banner` sits in the group's `block-start` addon, where the pattern puts
 * attachment chips. `footer` goes in the `block-end` addon ahead of Submit,
 * which carries `ml-auto` so it holds the trailing edge whatever the footer
 * holds, including nothing.
 */
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupTextarea } from "@repo/ui/components/ui/input-group";
import { ArrowUpIcon, SquareIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useRef } from "react";
import { chatMeasureStyle } from "../elements/chat-measure";
import { useAgentChatContext } from "./context";
import { useAgentChatSettingsOptional } from "./settings";

export interface AgentChatPromptInputProps {
  placeholder?: string;
  /** Rendered above the textarea, inside the composer's border. */
  banner?: ReactNode;
  /** Controls placed before Submit in the bottom row. */
  footer?: ReactNode;
}

export function AgentChatPromptInput({
  placeholder = "Message the assistant…",
  banner,
  footer,
}: AgentChatPromptInputProps) {
  const { state, actions, meta } = useAgentChatContext();
  // Optional: the provider app composes no `AgentChat.Settings`. When one is
  // present its model folds into whatever thread this submit creates.
  const settings = useAgentChatSettingsOptional();
  const formRef = useRef<HTMLFormElement>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!state.canSubmit) {
      return;
    }
    actions.submit(settings ? { model: settings.model } : undefined);
  }

  return (
    // The same cap and gutter `Conversation` gives the transcript, so a turn's
    // text and the box it is typed into share two vertical lines.
    <div data-chat-measure="composer" style={chatMeasureStyle}>
      <form ref={formRef} onSubmit={onSubmit}>
        <InputGroup>
          {banner && (
            <InputGroupAddon align="block-start" className="border-b">
              {banner}
            </InputGroupAddon>
          )}
          <InputGroupTextarea
            ref={meta.inputRef}
            value={state.draft}
            onChange={(event) => actions.setDraft(event.target.value)}
            placeholder={placeholder}
            onKeyDown={(event) => {
              // `isComposing`: Enter that confirms an IME candidate is not a send.
              if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault();
                formRef.current?.requestSubmit();
              }
            }}
            className="max-h-48 p-3"
          />
          <InputGroupAddon align="block-end">
            {footer}
            <AgentChatSubmit />
          </InputGroupAddon>
        </InputGroup>
      </form>
    </div>
  );
}

/** Send while idle; while a reply streams, the same slot stops it. */
export function AgentChatSubmit() {
  const { state, actions } = useAgentChatContext();
  if (state.isStreaming) {
    return (
      <InputGroupButton
        type="button"
        size="icon-sm"
        variant="default"
        className="ml-auto rounded-full"
        aria-label="Stop"
        onClick={() => actions.stop()}
      >
        <SquareIcon />
      </InputGroupButton>
    );
  }
  return (
    <InputGroupButton
      type="submit"
      size="icon-sm"
      variant="default"
      className="ml-auto rounded-full"
      disabled={!state.canSubmit}
      aria-label="Send"
    >
      <ArrowUpIcon />
    </InputGroupButton>
  );
}
